import React, { useMemo, useState } from "react";
import { BarChart } from "@mui/x-charts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { Chart } from "../../../Assessment/Graphing/Chart";
import MonthRangeSelector from "../../../../components/pearli/MonthRangeSelector.jsx";
import useAssessmentReport, { REPORT_FIELDS } from "../../../../hooks/assessment/useAssessmentReport";
import Markdown from "react-markdown";
import useUserRangeAverages from "../../../../hooks/assessment/useUserRangeAverages";
import {
  compareMonthKeys,
  getDefaultRangeMonthKeys,
  getRecentMonthOptions,
  getValidatedMonthRange,
  isSelectableMonthKey,
  monthKeyToDate,
} from "../../../../utils/dateUtils.js";
import AIDisclaimer from "../../../../components/common/AIDisclaimer.jsx";

const DOMAINS = ["Physical", "Emotional", "Acceptance", "Relationship", "Loving-Kindness", "Intellectual"];
const GREEN = "#19B880";
const GRAY = "#334155";
const RED = "#EF4444";

// component
const AssessmentHistory = ({ homeTabMode = 3 }) => {
  // Data view mode: "current" or "range"
  const [dataViewMode, setDataViewMode] = useState("current");

  const today = useMemo(() => new Date(), []);
  const monthOptions = useMemo(() => getRecentMonthOptions({ anchorDate: today }), [today]);
  const defaultMonthRange = useMemo(
    () => getDefaultRangeMonthKeys(today),
    [today]
  );

  const [rangeMonthSelection, setRangeMonthSelection] = useState(defaultMonthRange);

  const { startKey: rangeStartMonth, endKey: rangeEndMonth } = useMemo(
    () =>
      getValidatedMonthRange({
        startKey: rangeMonthSelection.startKey,
        endKey: rangeMonthSelection.endKey,
        anchorDate: today,
      }),
    [rangeMonthSelection.endKey, rangeMonthSelection.startKey, today]
  );

  const rangeStart = useMemo(() => monthKeyToDate(rangeStartMonth), [rangeStartMonth]);
  const rangeEnd = useMemo(() => monthKeyToDate(rangeEndMonth), [rangeEndMonth]);

  const { analysis, metadata, scores, loading, error, hasData } = useAssessmentReport();

  // Use the user range averages hook for comparison
  // Pass custom dates if user selected them, otherwise defaults to M-2 vs M-1
  const { monthBefore, previousMonth, loading: rangeLoading, error: rangeError, hasData: rangeHasData } = useUserRangeAverages(
    rangeStart,
    rangeEnd
  );

  const summaries = useMemo(() => REPORT_FIELDS.map((field) => analysis?.[field] || ""), [analysis]);

  // Expand toggles
  const [isPExpanded, setIsPExpanded] = useState(false);
  const [isEExpanded, setIsEExpanded] = useState(false);
  const [isAExpanded, setIsAExpanded] = useState(false);
  const [isRExpanded, setIsRExpanded] = useState(false);
  const [isLExpanded, setIsLExpanded] = useState(false);
  const [isIExpanded, setIsIExpanded] = useState(false);

  // Helper function to get domain key from domain label
  const getDomainKey = (label) => {
    if (label === "Relationship") return "relationships";
    if (label === "Intellectual") return "intellectual";
    return label.toLowerCase().replace("-", "_");
  };

  // Convert range averages to array format for BarChart
  const rangeComparisonScores = useMemo(() => {
    if (!monthBefore || !previousMonth) {
      return [[0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0]];
    }

    const monthBeforeScores = DOMAINS.map(label => {
      const key = getDomainKey(label);
      return Number(monthBefore.averages?.[key] || 0);
    });

    const previousMonthScores = DOMAINS.map(label => {
      const key = getDomainKey(label);
      return Number(previousMonth.averages?.[key] || 0);
    });

    return [monthBeforeScores, previousMonthScores];
  }, [monthBefore, previousMonth]);

  // Helper function for change color
  const changeColor = (delta) => (delta > 0 ? GREEN : delta === 0 ? GRAY : RED);

  const applyRangeSelection = (nextStartMonth, nextEndMonth) => {
    if (
      !isSelectableMonthKey(nextStartMonth, { anchorDate: today }) ||
      !isSelectableMonthKey(nextEndMonth, { anchorDate: today })
    ) {
      return;
    }

    if (compareMonthKeys(nextStartMonth, nextEndMonth) > 0) {
      return;
    }

    setRangeMonthSelection({
      startKey: nextStartMonth,
      endKey: nextEndMonth,
    });
  };

  const handleStartMonthChange = (nextStartMonth) => {
    const nextEndMonth =
      rangeEndMonth && compareMonthKeys(nextStartMonth, rangeEndMonth) > 0
        ? nextStartMonth
        : rangeEndMonth;

    applyRangeSelection(nextStartMonth, nextEndMonth);
  };

  const handleEndMonthChange = (nextEndMonth) => {
    const nextStartMonth =
      rangeStartMonth && compareMonthKeys(rangeStartMonth, nextEndMonth) > 0
        ? nextEndMonth
        : rangeStartMonth;

    applyRangeSelection(nextStartMonth, nextEndMonth);
  };

  return (
    <div
      className="home-statistics-section"
      style={{ display: homeTabMode === 3 ? "flex" : "none" }}
    >
      {/* Mobile message */}
      <div className="home-statistics-group-container-mobile">
        <div className="home-statistics-section-text-container-mobile">
          <div className="home-statistics-section-title">
            Assessment History Unavailable on Mobile
          </div>
          <div className="home-statistics-section-subtitle">
            We're sorry, but the Assessment History page is currently not
            supported on mobile devices. For the best experience and full
            functionality, please access this page from a desktop or laptop
            computer.
          </div>
        </div>
      </div>

      {/* Desktop content */}
      <div className="home-statistics-group-container">
        <div className="home-statistics-section-container">
          {/* Header */}
          <div className="home-statistics-section-header">
            <div className="home-statistics-section-text-container">
              <div className="home-statistics-section-title">
                Assessment History
              </div>
              <div className="home-statistics-section-subtitle">
                View your well-being journey over time
              </div>
            </div>
            <div className="home-menu-container">
              <div className="home-statistics-section-interval-button-container">
                <div
                  className={`home-statistics-section-interval-button ${dataViewMode === "current" ? "active" : ""}`}
                  onClick={() => setDataViewMode("current")}
                >
                  Current Month
                </div>
                <div
                  className={`home-statistics-section-interval-button ${dataViewMode === "range" ? "active" : ""}`}
                  onClick={() => setDataViewMode("range")}
                >
                  Month Range
                </div>
              </div>
              <div
                // className="home-daterange-button"
                id="daterange"
                style={{ display: dataViewMode === "range" ? "flex" : "none" }}
              >
                <MonthRangeSelector
                  monthOptions={monthOptions}
                  startMonth={rangeStartMonth}
                  endMonth={rangeEndMonth}
                  onChangeStart={handleStartMonthChange}
                  onChangeEnd={handleEndMonthChange}
                  sx={{ mt: 0 }}
                  anchorDate={today}
                />
              </div>
            </div>
          </div>

          {/* LATEST ASSESSMENT VIEW  */}
          <div style={{ display: dataViewMode === "current" ? "flex" : "none", flexDirection: "column", width: "100%", gap: "16px" }}>
            {error && (
              <div className="home-statistics-section-error-message" style={{
                padding: "20px",
                backgroundColor: "#FEF2F2",
                color: "#B91C1C",
                borderRadius: "8px",
                marginBottom: "20px"
              }}>
                {error}
              </div>
            )}

            {!hasData && !loading && !error && (
              <div className="home-statistics-section-empty-state" style={{
                padding: "40px",
                textAlign: "center",
                backgroundColor: "#F9FAFB",
                borderRadius: "8px",
                marginBottom: "20px"
              }}>
                <div style={{ fontSize: "18px", fontWeight: "600", marginBottom: "8px" }}>
                  No Recent Assessment
                </div>
                <div style={{ fontSize: "14px", color: "#6B7280" }}>
                  Complete an assessment to view your PEARLI scores and insights.
                </div>
              </div>
            )}

            <div className="home-statistics-section-item-container" style={{ width: "100%", margin: 0 }}>
              <div className="home-statistics-section-item-pearli-graph">
                <div className="home-statistics-section-item-header">
                  <div className="home-statistics-section-item-title">
                    Latest PEARLI Assessment Scores
                  </div>
                  <div className="home-statistics-section-item-subtitle">
                    {metadata?.assessmentDate ? `Assessment from ${new Date(metadata.assessmentDate).toLocaleDateString()}` : "Your most recent assessment snapshot"}
                  </div>
                </div>
                <div className="home-statistics-section-item-body-container-summary">
                  {metadata?.complete ? <Chart scores={scores} /> : <p>Complete all six sections to view the full comparison chart.</p>}
                  <div
                    className="home-statistics-section-item-header"
                    style={{ alignSelf: "center" }}
                  >
                    Average Score:{" "}
                    {loading
                      ? "Loading..."
                      : metadata?.complete
                      ? (scores.reduce((a, b) => a + Number(b || 0), 0) / 6).toFixed(1) + "/5.0"
                      : "Available after all six sections"}
                  </div>
                </div>
              </div>

              <div className="home-statistics-section-item-pearli-summary">
                <div className="home-statistics-section-item-header">
                  <div className="home-statistics-section-item-title">
                    PEARLI Score Summary
                  </div>
                  <div className="home-statistics-section-item-subtitle">
                    Your latest assessment overview
                  </div>
                </div>

                {loading && (
                  <div className="home-statistics-section-pearli-summary-body" id="summaryloading">
                    Loading Assessment Summary...
                  </div>
                )}

                {!hasData && !loading && (
                  <div className="home-statistics-section-pearli-summary-body">
                    No assessment data available. Please complete an assessment.
                  </div>
                )}

                <div
                  className="home-statistics-section-item-body-pearli-container"
                  id="pearlisummaries"
                  style={{ display: hasData && !loading ? "block" : "none" }}
                >
                  {DOMAINS.map((label, i) => {
                    const expandedToggles = [
                      [isPExpanded, setIsPExpanded],
                      [isEExpanded, setIsEExpanded],
                      [isAExpanded, setIsAExpanded],
                      [isRExpanded, setIsRExpanded],
                      [isLExpanded, setIsLExpanded],
                      [isIExpanded, setIsIExpanded],
                    ];
                    const [expanded, toggle] = expandedToggles[i];
                    const color =
                      scores[i] === null ? GRAY : (scores[i] || 0) >= 3.5
                        ? "#19B880"
                        : (scores[i] || 0) < 2.5
                          ? "#EF4444"
                          : "#EAB308";

                    return (
                      <div
                        key={label}
                        className={
                          "home-statistics-section-pearli-summary-container" +
                          Math.round(scores[i] || 0)
                        }
                      >
                        <div className="home-statistics-section-pearli-summary-title-caret-container">
                          <div className="home-statistics-section-pearli-summary-title">
                            <p>{label === "Relationship" ? "Relationships" : label}</p>
                          </div>
                          <div
                            className="home-statistics-section-pearli-summary-number"
                            style={{ color }}
                          >
                            {scores[i] === null ? "Not assessed" : Number(scores[i]).toFixed(1)}
                          </div>
                          <div className="home-statistics-section-pearli-summary-subnumber">
                            {scores[i] !== null ? "/5.0" : ""}
                          </div>
                          <div
                            className="home-statistics-section-pearli-summary-title-caret-icon"
                            onClick={() => toggle((v) => !v)}
                          >
                            <FontAwesomeIcon icon={expanded ? faCaretUp : faCaretDown} />
                          </div>
                        </div>
                        <div
                          className="home-statistics-section-pearli-summary-body"
                          style={{ display: expanded ? "flex" : "none" }}
                        >
                          <Markdown>{summaries[i]}</Markdown>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="home-statistics-section-item-pearli-overall-summary">
                <div className="home-statistics-section-item-header">
                  <div className="home-statistics-section-item-title">
                    PEARLI Overall Score Summary
                  </div>
                  <div className="home-statistics-section-item-subtitle">
                    Your next step in improving your well-being
                  </div>
                </div>
                <div className="home-statistics-section-item-body-container-summary">
                  <div className="home-statistics-section-item-body-text-summary">
                    {hasData ? <Markdown>{summaries[6]}</Markdown> : <p>Complete an assessment to receive personalized insights.</p>}
                  </div>
                </div>
              </div>
            </div>

            {hasData && (
              <AIDisclaimer
                variant="banner"
                title="AI Wellbeing Insights Disclaimer"
                text="This summary is generated by artificial intelligence based on your latest PEARLI assessment scores to support personal self-reflection. It is not intended as medical, clinical, or psychiatric advice."
                sx={{ width: "100%", mt: 0.5 }}
              />
            )}
          </div>

          {/* MONTHLY COMPARISON VIEW  */}
          <div className="home-statistics-section-item-container" style={{ display: dataViewMode === "range" ? "flex" : "none" }}>
            {rangeError && (
              <div className="home-statistics-section-error-message" style={{
                padding: "20px",
                backgroundColor: "#FEF2F2",
                color: "#B91C1C",
                borderRadius: "8px",
                marginBottom: "20px"
              }}>
                {rangeError}
              </div>
            )}

            {!rangeHasData && !rangeLoading && !rangeError && (
              <div className="home-statistics-section-empty-state" style={{
                padding: "40px",
                textAlign: "center",
                backgroundColor: "#F9FAFB",
                borderRadius: "8px",
                marginBottom: "20px"
              }}>
                <div style={{ fontSize: "18px", fontWeight: "600", marginBottom: "8px" }}>
                  Not Enough Data to Compare
                </div>
                <div style={{ fontSize: "14px", color: "#6B7280" }}>
                  Complete assessments across multiple months to see comparisons.
                </div>
              </div>
            )}

            {rangeHasData && (
              <>
                <div className="home-statistics-section-item-w-bargraph">
                  <div className="home-statistics-section-item-header">
                    <div className="home-statistics-section-item-title">
                      PEARLI Assessment Comparison ({monthBefore?.month} – {previousMonth?.month})
                    </div>
                    <div className="home-statistics-section-item-subtitle">
                      Data reflects average PEARLI assessment score trends over time
                    </div>
                  </div>
                  <div className="home-statistics-section-item-body-text" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <BarChart
                      loading={rangeLoading}
                      xAxis={[{ scaleType: "band", data: DOMAINS }]}
                      yAxis={[{ label: "Average Score", width: 50 }]}
                      series={[
                        {
                          data: rangeComparisonScores[0],
                          color: "#3B82F6",
                          label: monthBefore?.month,
                        },
                        {
                          data: rangeComparisonScores[1],
                          color: "#0C8F63",
                          label: previousMonth?.month,
                        },
                      ]}
                      height={330}
                    />
                  </div>
                </div>

                <div className="home-statistics-section-item-table-container">
                  <div className="home-statistics-section-item-header">
                    <div className="home-statistics-section-item-title">
                      PEARLI Assessment Score Summary ({monthBefore?.month} – {previousMonth?.month})
                    </div>
                    <div className="home-statistics-section-item-subtitle">
                      Showing trends over time
                    </div>
                  </div>

                  {/* Header row */}
                  <div className="home-statistics-section-table-header-container">
                    <div
                      className="home-statistics-section-table-headers-text"
                      style={{ width: "26%", fontSize: "14px", fontWeight: 700 }}
                    >
                      Domain
                    </div>
                    <div className="home-statistics-section-table-headers-text" style={{ width: "18.5%", textAlign: "center", fontSize: "14px", fontWeight: 700 }}>
                      {monthBefore?.month}
                    </div>
                    <div className="home-statistics-section-table-headers-text" style={{ width: "18.5%", textAlign: "center", fontSize: "14px", fontWeight: 700 }}>
                      {previousMonth?.month}
                    </div>
                    <div className="home-statistics-section-table-headers-text" style={{ width: "18.5%", textAlign: "center", fontSize: "14px", fontWeight: 700 }}>
                      Change
                    </div>
                    <div className="home-statistics-section-table-headers-text" style={{ width: "18.5%", textAlign: "center", fontSize: "14px", fontWeight: 700 }}>
                      Change(%)
                    </div>
                  </div>

                  {/* Data rows */}
                  {DOMAINS.map((label, i) => {
                    const prev = Number(rangeComparisonScores[0][i] || 0);
                    const curr = Number(rangeComparisonScores[1][i] || 0);
                    const delta = Number((curr - prev).toFixed(1));
                    const pct = prev > 0 ? (((curr - prev) / prev) * 100).toFixed(1) + "%" : "N/A";
                    const isPositive = curr > prev;
                    const isNegative = curr < prev;
                    const changeColorVal = isPositive ? "#10B981" : isNegative ? "#EF4444" : "#64748B";

                    return (
                      <div
                        className="home-statistics-section-table-row-container"
                        key={label}
                      >
                        <div
                          className="home-statistics-section-table-body-text"
                          style={{ width: "26%", fontWeight: 500, color: "#1E293B" }}
                        >
                          {label === "Relationship" ? "Relationships" : label}
                        </div>
                        <div className="home-statistics-section-table-body-text" style={{ width: "18.5%", justifyContent: "center" }}>
                          <span style={{ fontWeight: 600, color: "#1E293B" }}>{prev.toFixed(1)}</span>
                          <span className="home-statistics-section-table-body-subtext">&nbsp;/5.0</span>
                        </div>
                        <div className="home-statistics-section-table-body-text" style={{ width: "18.5%", justifyContent: "center" }}>
                          <span style={{ fontWeight: 600, color: "#1E293B" }}>{curr.toFixed(1)}</span>
                          <span className="home-statistics-section-table-body-subtext">&nbsp;/5.0</span>
                        </div>
                        <div
                          className="home-statistics-section-table-body-text"
                          style={{ width: "18.5%", justifyContent: "center", color: changeColorVal, fontWeight: 600 }}
                        >
                          <p>{delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1)}</p>
                        </div>
                        <div
                          className="home-statistics-section-table-body-text"
                          style={{ width: "18.5%", justifyContent: "center", color: changeColorVal, fontWeight: 600 }}
                        >
                          <p>{delta > 0 ? `+${pct}` : pct}</p>
                        </div>
                      </div>
                    );
                  })}

                  {/* Overall Score row */}
                  {rangeHasData && (
                    <div
                      className="home-statistics-section-table-row-container home-statistics-section-table-summary-row"
                    >
                      <div
                        className="home-statistics-section-table-body-text"
                        style={{ width: "26%", fontWeight: 700, color: "#0F172A" }}
                      >
                        Average Score
                      </div>
                      <div className="home-statistics-section-table-body-text" style={{ width: "18.5%", justifyContent: "center", fontWeight: 700, color: "#0F172A" }}>
                        {monthBefore?.overallScore ? Number(monthBefore.overallScore).toFixed(1) : "N/A"}
                      </div>
                      <div className="home-statistics-section-table-body-text" style={{ width: "18.5%", justifyContent: "center", fontWeight: 700, color: "#0F172A" }}>
                        {previousMonth?.overallScore ? Number(previousMonth.overallScore).toFixed(1) : "N/A"}
                      </div>
                      <div
                        className="home-statistics-section-table-body-text"
                        style={{
                          width: "18.5%",
                          justifyContent: "center",
                          fontWeight: 700,
                          color: changeColor(
                            (previousMonth?.overallScore || 0) - (monthBefore?.overallScore || 0)
                          ),
                        }}
                      >
                        <p>
                          {previousMonth?.overallScore && monthBefore?.overallScore
                            ? (Number(previousMonth.overallScore) - Number(monthBefore.overallScore) > 0 ? "+" : "") +
                              (Number(previousMonth.overallScore) - Number(monthBefore.overallScore)).toFixed(1)
                            : "N/A"}
                        </p>
                      </div>
                      <div
                        className="home-statistics-section-table-body-text"
                        style={{
                          width: "18.5%",
                          justifyContent: "center",
                          fontWeight: 700,
                          color: changeColor(
                            (previousMonth?.overallScore || 0) - (monthBefore?.overallScore || 0)
                          ),
                        }}
                      >
                        <p>
                          {previousMonth?.overallScore && monthBefore?.overallScore && monthBefore.overallScore > 0
                            ? (Number(previousMonth.overallScore) - Number(monthBefore.overallScore) > 0 ? "+" : "") +
                              (
                                ((Number(previousMonth.overallScore) - Number(monthBefore.overallScore)) /
                                  Number(monthBefore.overallScore)) *
                                100
                              ).toFixed(1) + "%"
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentHistory;
